import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format currency
export function formatCurrency(amount, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

// Format percentage
export function formatPercentage(value, showSign = true) {
  const formatted = Math.abs(value).toFixed(1);
  if (showSign && value !== 0) {
    return value > 0 ? `+${formatted}%` : `-${formatted}%`;
  }
  return `${formatted}%`;
}

// Format large numbers
export function formatNumber(num) {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

// Get currency flag emoji
export function getCurrencyFlag(currency) {
  const flags = {
    USD: '🇺🇸',
    EUR: '🇪🇺',
    GBP: '🇬🇧',
    JPY: '🇯🇵',
    AUD: '🇦🇺',
    CAD: '🇨🇦',
    CHF: '🇨🇭',
    CNY: '🇨🇳',
    NZD: '🇳🇿',
    SEK: '🇸🇪',
  };
  return flags[currency] || '🌐';
}

// Get impact color class
export function getImpactClass(impact) {
  switch (impact?.toLowerCase()) {
    case 'high':
      return 'impact-high';
    case 'medium':
      return 'impact-medium';
    case 'low':
    default:
      return 'impact-low';
  }
}

// Get premium tier gradient
export function getTierClass(tier) {
  switch (tier?.toLowerCase()) {
    case 'diamond':
      return 'tier-diamond';
    case 'platinum':
      return 'tier-platinum';
    case 'gold':
      return 'tier-gold';
    default:
      return 'bg-muted';
  }
}

// Truncate text
export function truncateText(text, maxLength) {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

// Format date
export function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Format time
export function formatTime(timeString) {
  return timeString;
}

// Get relative time
export function getRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDate(dateString);
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Generate broker score between 0 and 5.5 based on broker ID
export function generateBrokerScore(brokerId) {
  let hash = 0;
  const str = brokerId || 'default';
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash % 550) / 100;
}

export function generateTrustScore(brokerId) {
  let hash = 0;
  const str = (brokerId || 'default') + '_trust';
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 7) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash % 570) / 100;
}

// Generate deterministic score per dimension using broker ID + dimension seed
function hashScore(brokerId, seed, min, max) {
  let hash = 0;
  const str = (brokerId || 'default') + seed;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  const range = (max - min) * 100;
  return min + Math.abs(hash % range) / 100;
}

// FX Astra Rating Index — 5 dimensions scored 0-10
export function generateBrokerRatingIndex(broker) {
  const id = broker?.id || 'default';
  const regsCount = broker?.regulations?.length || 0;
  const hasRealReg = broker?.regulations?.some(r => !['Unregulated', 'None', 'N/A'].includes(r));

  // 1. Regulation (weight: 25%) — based on number of regulators + quality
  const regBase = hasRealReg ? Math.min(regsCount * 2.5, 8) : hashScore(id, '_reg', 1, 4);
  const regulation = Math.min(10, regBase + hashScore(id, '_regx', 0, 2));

  // 2. Trading (weight: 20%) — spreads, leverage, account variety
  const spreadScore = broker?.spreads_from != null ? Math.max(0, 10 - broker.spreads_from * 2) : hashScore(id, '_spr', 4, 8);
  const accScore = Math.min(10, (broker?.account_types?.length || 1) * 3);
  const trading = (spreadScore * 0.6 + accScore * 0.4);

  // 3. Platforms (weight: 20%) — number and variety of platforms
  const platCount = broker?.platforms?.length || 0;
  const platBase = Math.min(10, platCount * 2.5 + hashScore(id, '_plat', 1, 3));
  const platforms = Math.min(10, platBase);

  // 4. Trust & Safety (weight: 20%) — trust_score + user_rating + years
  const years = broker?.founded_year ? Math.min(30, 2026 - broker.founded_year) : 5;
  const yearScore = Math.min(10, years * 0.4);
  const trustBase = broker?.trust_score != null ? broker.trust_score : hashScore(id, '_tru', 3, 7);
  const trust = Math.min(10, (trustBase + yearScore + hashScore(id, '_trux', 0, 2)) / 1.5);

  // 5. Support (weight: 15%) — methods, hours
  const depCount = broker?.deposit_methods?.length || 0;
  const wdCount = broker?.withdrawal_methods?.length || 0;
  const is24h = broker?.support_hours?.includes('24') ? 2 : 0;
  const support = Math.min(10, depCount + wdCount + is24h + hashScore(id, '_sup', 2, 4));

  const overall = (regulation * 0.25 + trading * 0.20 + platforms * 0.20 + trust * 0.20 + support * 0.15);

  return {
    regulation: +regulation.toFixed(2),
    trading: +trading.toFixed(2),
    platforms: +platforms.toFixed(2),
    trust: +trust.toFixed(2),
    support: +support.toFixed(2),
    overall: +overall.toFixed(2),
  };
}
